import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGame } from "../services/gameContext";
import { subscribeAuth } from "../services/auth";
import { subscribeForum, postForumMessage, FORUM_DEPTH } from "../services/forum";
import ChatDrawer from "./ChatDrawer";

// Same key Auth.jsx reads after successful sign-in — set it before
// sending the user to /signin so Google / email sign-in redirects
// them back to the forum instead of the default /me landing.
const POST_AUTH_TARGET_KEY = "popmarket-post-auth-target";
function stashReturnHere() {
  try {
    const path = (window.location.pathname || "/") + (window.location.search || "");
    localStorage.setItem(POST_AUTH_TARGET_KEY, path);
  } catch (_) { /* localStorage unavailable, no big deal */ }
}

// Community forum on Directory:
//   - Two tabs (Orders / Chat). Signed-in users see a compose form
//     at the top of the active tab; anon users see a sign-in nudge.
//   - Rolling window of 100 messages, live-updating via onSnapshot.
//   - Buyer / seller / both roles rendered as a coloured pill next
//     to the author.

const SEGMENTS = [
  { key: "mix",        label: "Mix" },
  { key: "premium",    label: "Premium" },
  { key: "lumen",      label: "Lumen" },
  { key: "helicopter", label: "Helicopter" },
];
const ROLE_BADGE = {
  seller: { label: "💎 SELLER", color: "#4de37c", bg: "rgba(43,181,98,0.14)" },
  buyer:  { label: "🎯 BUYER",  color: "#6bb6ff", bg: "rgba(60,140,220,0.16)" },
  both:   { label: "🔁 BOTH",   color: "#e3a857", bg: "rgba(227,168,87,0.14)" },
};
const ACCENTS = { BGMI: "#F88200", PUBG: "#258BC8" };

function fmtRelTime(ts) {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 45) return "just now";
  if (s < 90) return "1 min ago";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 7200) return "1 hr ago";
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Split a raw pop count into { number, unit } for styled display:
//   >= 1M   → number in millions, unit "M"   (rendered rainbow)
//   >= 1k   → number in thousands, unit "K"  (rendered gold)
//   <  1k   → raw, no unit                    (rendered white)
function splitQty(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return { number: "", unit: null };
  if (n >= 1_000_000) return { number: (n / 1_000_000).toFixed(1), unit: "M" };
  if (n >= 1_000)     return { number: (n / 1_000).toFixed(0),     unit: "K" };
  return { number: String(n), unit: null };
}

// Styled quantity display. K uses a rich golden gradient; M uses a
// rainbow gradient with a subtle animated sheen so millions-scale
// orders visually pop.
function QtyDisplay({ value, size = "sm" }) {
  const { number, unit } = splitQty(value);
  if (!number) return null;
  const isM = unit === "M";
  const isK = unit === "K";
  const fontSize = size === "lg" ? 18 : 14;
  const unitCls = isM ? "pmf-qty-rainbow" : isK ? "pmf-qty-gold" : "";
  return (
    <span
      style={{
        fontSize,
        fontWeight: 900,
        letterSpacing: 0.4,
        display: "inline-flex",
        alignItems: "baseline",
        gap: 1,
        color: unit ? undefined : "#eaeaef",
      }}
    >
      <span className={unitCls}>{number}</span>
      {unit && <span className={unitCls} style={{ marginLeft: 2 }}>{unit}</span>}
    </span>
  );
}

export default function ForumFeed() {
  const { game } = useGame();
  const accent = ACCENTS[game] || "#4c6ef5";
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  useEffect(() => subscribeAuth(setUser), []);

  const [tab, setTab] = useState("order");  // "order" | "message"
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    const unsub = subscribeForum({ game, kindFilter: tab }, setMsgs);
    return unsub;
  }, [game, tab]);

  // In-app chat state — one drawer at a time. Populated when a signed-
  // in visitor clicks "💬 CHAT" on another user's order.
  const [chatTarget, setChatTarget] = useState(null);
  const openChatWith = (msg) => {
    if (!user || user.isAnonymous) {
      stashReturnHere();
      navigate("/signin");
      return;
    }
    if (!msg.authorPaUid || msg.authorPaUid === user.uid) return;
    setChatTarget({
      other: {
        uid: msg.authorPaUid,
        username: msg.authorUsername || msg.authorHandle || null,
      },
      orderContext: {
        game: msg.game,
        quantity: msg.quantity,
        segment: msg.segment,
        maxRate: msg.maxRate,
        forumMsgId: msg.id,
      },
    });
  };
  const closeChat = () => setChatTarget(null);

  return (
    <section
      className="pmf-root"
      style={{
        marginBottom: 24,
        padding: 16,
        borderRadius: 14,
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.06)",
        overflow: "hidden",  // stop inner content from bleeding sideways
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, color: accent }}>
          COMMUNITY FEED
        </span>
        <span
          style={{
            fontSize: 10, fontWeight: 800, letterSpacing: 1.4,
            padding: "3px 9px", borderRadius: 6,
            background: accent, color: "#0d1017",
          }}
        >
          {game}
        </span>
        <span style={{ fontSize: 10.5, color: "#7a7d86", letterSpacing: 0.8, marginLeft: "auto" }}>
          latest {FORUM_DEPTH}
        </span>
      </div>

      {/* Tab bar — stretches full-width on mobile so the ORDERS /
          CHAT tabs each get half the row instead of two 96px pills
          floating on the left. */}
      <div className="pmf-tabs" style={{
        display: "flex", gap: 0, borderRadius: 6, overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.10)",
        background: "rgba(255,255,255,0.02)", marginBottom: 12,
        width: "fit-content",
      }}>
        {[
          { key: "order",   label: "ORDERS" },
          { key: "message", label: "CHAT" },
        ].map((t, idx) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                flex: "1 1 0",
                padding: "8px 20px",
                minWidth: 96,
                border: "none",
                borderLeft: idx > 0 ? "1px solid rgba(255,255,255,0.10)" : "none",
                borderBottom: active ? `3px solid ${accent}` : "3px solid transparent",
                background: active ? `${accent}22` : "transparent",
                color: active ? "#fff" : "#9ca0a8",
                fontWeight: 900, letterSpacing: 1.6, fontSize: 12,
                cursor: "pointer", textTransform: "uppercase", lineHeight: 1,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <style>{`
        @media (max-width: 560px) {
          .pmf-root { padding: 12px !important; }
          .pmf-tabs { width: 100% !important; }
        }
        /* K = solid molten gold with a slow directional sheen so the
           highlight sweeps left→right across the number. */
        .pmf-qty-gold {
          background: linear-gradient(
            90deg,
            #B7791F 0%, #FFD86B 30%, #FFF1B8 50%,
            #FFD86B 70%, #B7791F 100%
          );
          background-size: 220% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          text-shadow: 0 1px 2px rgba(140, 90, 15, 0.35);
          animation: pmf-gold-sheen 3.2s ease-in-out infinite;
        }
        /* M = rainbow spectrum, saturated pop palette. Kept crisp —
           no white shine band inside the gradient, no tight white
           halo filter (both were reading as blur on smaller sizes).
           Shimmer is just the coloured spectrum scrolling. */
        .pmf-qty-rainbow {
          background: linear-gradient(
            90deg,
            #ff2d5c, #ff8a2a, #ffde3a, #4dff9a,
            #26c6ff, #a561ff, #ff2d8a, #ff2d5c
          );
          background-size: 260% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          filter: drop-shadow(0 0 6px rgba(255, 180, 60, 0.30));
          animation: pmf-rainbow-shimmer 2.6s linear infinite;
        }
        @keyframes pmf-gold-sheen {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes pmf-rainbow-shimmer {
          0%   { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .pmf-qty-gold, .pmf-qty-rainbow { animation: none; }
        }
      `}</style>

      {/* Compose (signed-in only) or sign-in nudge */}
      {user && !user.isAnonymous ? (
        <ComposeForm accent={accent} game={game} kind={tab} onPosted={() => {}} />
      ) : (
        <div
          style={{
            padding: "10px 14px", marginBottom: 12,
            borderRadius: 10,
            background: "rgba(255,255,255,0.03)",
            border: "1px dashed rgba(255,255,255,0.15)",
            fontSize: 13, color: "#c8ced5",
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          }}
        >
          <span>Sign in to post {tab === "order" ? "an order" : "in the chat"}.</span>
          <Link
            to="/signin"
            onClick={stashReturnHere}
            style={{ color: accent, fontWeight: 700, textDecoration: "underline" }}
          >
            Sign in →
          </Link>
        </div>
      )}

      {/* Message list — max-height uses viewport so it doesn't
          eat the whole mobile screen; content scrolls inside. */}
      <div
        className="pmf-msg-list"
        style={{
          display: "flex", flexDirection: "column", gap: 8,
          maxHeight: "clamp(320px, 60vh, 560px)",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {msgs.length === 0 ? (
          <div style={{ padding: "26px 14px", textAlign: "center", color: "#7a7d86", fontSize: 13, fontStyle: "italic" }}>
            {tab === "order"
              ? "No open orders yet — be the first."
              : "Nothing in chat yet."}
          </div>
        ) : (
          msgs.map((m) => (
            <ForumMessage
              key={m.id}
              msg={m}
              accent={accent}
              currentUid={user?.uid || null}
              onChat={() => openChatWith(m)}
            />
          ))
        )}
      </div>

      {chatTarget && user && (
        <ChatDrawer
          me={user}
          other={chatTarget.other}
          orderContext={chatTarget.orderContext}
          onClose={closeChat}
        />
      )}
    </section>
  );
}

function ComposeForm({ accent, game, kind, onPosted }) {
  const [quantity, setQuantity] = useState("");
  const [segment, setSegment] = useState("mix");
  const [maxRate, setMaxRate] = useState("");
  const [text, setText] = useState("");
  const [status, setStatus] = useState("idle"); // idle | posting | error
  const [error, setError] = useState(null);

  const isOrder = kind === "order";
  const canPost = isOrder
      ? Number(quantity) > 0
      : text.trim().length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canPost || status === "posting") return;
    setStatus("posting");
    setError(null);
    try {
      // The compose input is in K (thousands), matching how sellers
      // and buyers actually talk ("100k mix"). Multiply by 1000 so
      // the stored quantity is the raw pop count — matches sellers/
      // buyers listing schema and keeps fmtQty happy.
      const quantityRaw = isOrder ? Math.round(Number(quantity) * 1000) : undefined;
      await postForumMessage({
        kind, game,
        quantity: quantityRaw,
        segment: isOrder ? segment : undefined,
        maxRate: isOrder && maxRate ? Number(maxRate) : undefined,
        text: text.trim() || undefined,
      });
      setQuantity(""); setMaxRate(""); setText("");
      setStatus("idle");
      if (onPosted) onPosted();
    } catch (err) {
      setError(err.message || String(err));
      setStatus("error");
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{
        marginBottom: 12,
        padding: 12,
        borderRadius: 10,
        background: "rgba(255,255,255,0.03)",
        border: `1px solid ${accent}44`,
      }}
    >
      {isOrder ? (
        <div className="pmf-order-row">
          {/* Quantity input is in K (thousands). "150" means 150,000
              pop — matches how sellers and buyers talk about it. The
              suffix is baked into the field so users don't guess. */}
          <div style={{ position: "relative", minWidth: 0 }}>
            <input
              type="number" min="1" max="10000" step="1"
              placeholder="Pop needed *"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              style={{ ...inputStyle, paddingRight: 28, width: "100%", boxSizing: "border-box" }}
              required
            />
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 12,
                fontWeight: 800,
                color: "#7a7d86",
                letterSpacing: 1,
                pointerEvents: "none",
              }}
            >
              K
            </span>
          </div>
          <select
            value={segment}
            onChange={(e) => setSegment(e.target.value)}
            style={inputStyle}
          >
            {SEGMENTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <input
            type="number" min="0" step="0.01"
            placeholder="Max ₹/1k (optional)"
            value={maxRate}
            onChange={(e) => setMaxRate(e.target.value)}
            style={inputStyle}
          />
        </div>
      ) : null}
      <textarea
        placeholder={isOrder ? "Add a note (optional)" : "Say something..."}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={isOrder ? 2 : 3}
        maxLength={300}
        style={{ ...inputStyle, marginTop: 8, resize: "vertical", width: "100%", boxSizing: "border-box" }}
      />
      <style>{`
        .pmf-order-row {
          display: grid;
          grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.3fr);
          gap: 8px;
        }
        @media (max-width: 560px) {
          .pmf-order-row {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          }
          .pmf-order-row > :nth-child(3) {
            grid-column: 1 / -1;
          }
        }
      `}</style>
      {error && (
        <div style={{ marginTop: 6, fontSize: 12, color: "#ff6f61" }}>
          ⚠️ {error}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
        <button
          type="submit"
          disabled={!canPost || status === "posting"}
          style={{
            padding: "8px 20px",
            borderRadius: 6,
            border: "none",
            background: canPost ? accent : "rgba(255,255,255,0.08)",
            color: canPost ? "#0d1017" : "#5c6069",
            fontWeight: 900, letterSpacing: 1.2, fontSize: 12,
            cursor: canPost && status !== "posting" ? "pointer" : "not-allowed",
          }}
        >
          {status === "posting" ? "POSTING…" : isOrder ? "PLACE ORDER" : "POST"}
        </button>
        <span style={{ fontSize: 11, color: "#7a7d86" }}>
          {text.length}/300
        </span>
      </div>
    </form>
  );
}

const inputStyle = {
  padding: "8px 10px",
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(0,0,0,0.25)",
  color: "#eaeaef",
  borderRadius: 6,
  fontSize: 13,
  minWidth: 0,
};

function ForumMessage({ msg, accent, currentUid, onChat }) {
  const badge = ROLE_BADGE[msg.authorRole];
  const isOrder = msg.kind === "order";
  // Prefer authorUsername (globally unique, resolves to /u/:username);
  // fall back to authorHandle for legacy messages posted before the
  // username was assigned server-side.
  const identity = msg.authorUsername || msg.authorHandle || null;
  const profileHref = msg.authorUsername
      ? `/u/${msg.authorUsername}`
      : msg.authorHandle
        ? `/seller/${msg.authorHandle}`
        : null;
  // In-website chat button. Enabled on orders when we have the poster's
  // uid AND the viewer isn't the poster themselves. Signed-out visitors
  // still see the button — the click handler routes them via /signin
  // with a stashed return target.
  const isMyOrder = currentUid && currentUid === msg.authorPaUid;
  const canChat = isOrder && !!msg.authorPaUid && !isMyOrder;

  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: 8,
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
        {identity && profileHref ? (
          <Link
            to={profileHref}
            style={{
              fontSize: 12.5, fontWeight: 700, color: "#eaeaef",
              wordBreak: "break-word", textDecoration: "none",
              borderBottom: `1px dashed ${accent}55`,
            }}
          >
            @{identity}
          </Link>
        ) : (
          <span style={{ fontSize: 12.5, fontWeight: 700, color: "#eaeaef" }}>
            {identity ? `@${identity}` : "Guest"}
          </span>
        )}
        {badge && (
          <span
            style={{
              fontSize: 9.5, fontWeight: 900, letterSpacing: 1.2,
              padding: "2px 7px", borderRadius: 999,
              background: badge.bg, color: badge.color,
              border: `1px solid ${badge.color}44`,
              whiteSpace: "nowrap",
            }}
          >
            {badge.label}
          </span>
        )}
        <span style={{ fontSize: 10.5, color: "#7a7d86", marginLeft: "auto", whiteSpace: "nowrap" }}>
          {fmtRelTime(msg.createdAt)}
        </span>
      </div>
      {isOrder && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: msg.text ? 6 : 0, alignItems: "baseline" }}>
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
            <QtyDisplay value={msg.quantity} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "#c8ced5", letterSpacing: 0.8 }}>
              {msg.segment ? msg.segment.toUpperCase() : ""}
            </span>
          </span>
          {typeof msg.maxRate === "number" && (
            <span style={{ fontSize: 13, color: "#c8ced5", whiteSpace: "nowrap" }}>
              @ ≤ ₹{msg.maxRate.toFixed(msg.maxRate >= 100 ? 0 : 2)}/1k
            </span>
          )}
        </div>
      )}
      {msg.text && (
        <div style={{ fontSize: 13, color: "#d0d3d9", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {msg.text}
        </div>
      )}
      {isOrder && (
        <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canChat ? (
            <button
              type="button"
              onClick={onChat}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 6,
                background: accent, color: "#0d1017",
                fontSize: 11.5, fontWeight: 900, letterSpacing: 1,
                border: "none", cursor: "pointer",
              }}
            >
              💬 CHAT
            </button>
          ) : isMyOrder ? (
            <span
              title="This is your own order"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 6,
                background: "rgba(255,255,255,0.05)", color: "#7a7d86",
                fontSize: 11.5, fontWeight: 900, letterSpacing: 1,
                border: "1px dashed rgba(255,255,255,0.10)",
              }}
            >
              YOUR ORDER
            </span>
          ) : null}
          {profileHref && (
            <Link
              to={profileHref}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 6,
                background: "rgba(255,255,255,0.04)", color: "#c8ced5",
                border: "1px solid rgba(255,255,255,0.10)",
                fontSize: 11.5, fontWeight: 800, letterSpacing: 0.8,
                textDecoration: "none",
              }}
            >
              VIEW PROFILE →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
